/**
 * Formats a number as currency with proper formatting
 * @param value - The numeric value to format
 * @param currency - The currency symbol (default: '$')
 * @param locale - The locale for formatting (default: 'en-US')
 * @returns Formatted currency string (e.g., "$15,761.00", "$0.00", "$100.00")
 */
export const formatCurrency = (
  value: number | string,
  currency: string = "$",
  locale: string = "en-US",
): string => {
  // Convert string to number if needed
  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  // Handle invalid numbers
  if (isNaN(numericValue)) {
    return `${currency}0.00`;
  }

  // Format the number with proper locale formatting
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD", // This will be overridden by the currency parameter
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Replace the default currency symbol with the provided one
  return formatter.format(numericValue).replace(/^\$/, currency);
};

/**
 * Formats a number as currency with a simpler approach
 * @param value - The numeric value to format
 * @param currency - The currency symbol (default: '$')
 * @returns Formatted currency string (e.g., "$15,761.00", "$0.00", "$100.00")
 */
export const formatCurrencySimple = (
  value: number | string,
  currency: string = "$",
): string => {
  // Convert string to number if needed
  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  // Handle invalid numbers
  if (isNaN(numericValue)) {
    return `${currency}0.00`;
  }

  // Format with commas and 2 decimal places
  const formatted = numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${currency}${formatted}`;
};
